import { useState } from 'react';
import { Button, Card, Col, DatePicker, Row, Select, Space, Tag, Tooltip, Typography } from 'antd';
import { DataTable } from '../../components/DataTable';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  ClipboardText,
  Coins,
  Info,
  Money,
  Receipt,
  WarningCircle,
} from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { PageBody, PageHeader } from '../../app/AppLayout';
import { ErrorState, LoadingState } from '../../components/PageStates';
import { StatusTag } from '../../components/StatusTag';
import { isSuperAdmin, useCurrentStaff } from '../../auth/session';
import { catalogApi, catalogKeys } from '../catalog/api';
import { tokens, tierColorFor } from '../../theme';
import {
  dashboardApi,
  dashboardKeys,
  type BranchPerformanceRow,
  type DashboardLiability,
  type DashboardScope,
  type TierDistributionBucket,
} from './api';
import { VND } from '../../format';

const REFETCH_MS = 30_000;
const TREND_DAYS = 7;

/**
 * Tách số tiền thành phần SỐ (hiện to) và ĐƠN VỊ (hiện nhỏ, tách khoảng cách) — như prototype
 * (`437,1` to, `triệu đ` nhỏ). Số lớn quy ra "triệu"; số nhỏ giữ nguyên đồng.
 */
function moneyParts(value: number): { amount: string; unit: string } {
  const oneDecimal = (n: number) => n.toLocaleString('vi-VN', { maximumFractionDigits: 1 });
  if (value >= 1_000_000_000) {
    return { amount: oneDecimal(value / 1_000_000_000), unit: 'tỷ đ' };
  }
  if (value >= 1_000_000) {
    return { amount: oneDecimal(value / 1_000_000), unit: 'triệu đ' };
  }
  return { amount: VND.format(value), unit: 'đ' };
}

/** Số tiền: phần số to, đơn vị nhỏ và cách ra để "đ" không dính vào số. */
function MoneyValue({ value }: { value: number }) {
  const { amount, unit } = moneyParts(value);
  return (
    <>
      {amount}
      <small style={{ fontSize: 14, fontWeight: 600, color: tokens.color.ink2, marginLeft: 6 }}>
        {unit}
      </small>
    </>
  );
}

function formatRefreshedAt(iso: string | null): string {
  if (!iso) return 'chưa có dữ liệu';
  const parts = new Intl.DateTimeFormat('vi-VN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(iso));
  const get = (t: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === t)?.value ?? '';
  return `cập nhật lúc ${get('hour')}:${get('minute')}`;
}

export function DashboardPage() {
  const navigate = useNavigate();
  const staff = useCurrentStaff();
  const hq = isSuperAdmin(staff);

  const [date, setDate] = useState<Dayjs>(dayjs());
  const [chainId, setChainId] = useState<string>('all');
  const [branchId, setBranchId] = useState<string>('all');

  const scope: DashboardScope = {
    date: date.format('YYYY-MM-DD'),
    chainId: chainId === 'all' ? undefined : chainId,
    branchId: branchId === 'all' ? undefined : branchId,
  };

  const chainsQuery = useQuery({
    queryKey: catalogKeys.chains,
    queryFn: catalogApi.listChains,
    enabled: hq,
  });
  const branchesQuery = useQuery({
    queryKey: catalogKeys.branches,
    queryFn: catalogApi.listBranches,
    enabled: hq,
  });

  const overviewQuery = useQuery({
    queryKey: dashboardKeys.overview(scope),
    queryFn: () => dashboardApi.overview(scope),
    refetchInterval: REFETCH_MS,
  });
  const trendQuery = useQuery({
    queryKey: dashboardKeys.trend(scope, TREND_DAYS),
    queryFn: () => dashboardApi.revenueTrend(scope, TREND_DAYS),
    refetchInterval: REFETCH_MS,
  });
  const tiersQuery = useQuery({
    queryKey: dashboardKeys.tiers,
    queryFn: dashboardApi.tierDistribution,
    refetchInterval: REFETCH_MS,
  });
  const branchesPerfQuery = useQuery({
    queryKey: dashboardKeys.branches(scope),
    queryFn: () => dashboardApi.branchPerformance(scope),
    refetchInterval: REFETCH_MS,
  });
  // Liability toàn hệ thống — không phụ thuộc scope ngày/chi nhánh (điểm tồn cộng dồn trọn đời).
  const liabilityQuery = useQuery({
    queryKey: dashboardKeys.liability,
    queryFn: dashboardApi.liability,
    refetchInterval: REFETCH_MS,
  });

  const overview = overviewQuery.data;
  const reviewCount =
    (overview?.manualScansPending ?? 0) +
    (overview?.reversalPendingScans ?? 0) +
    (overview?.flaggedScans ?? 0);

  // Lọc chi nhánh theo chuỗi đang chọn (chỉ super admin dùng bộ lọc này).
  const branchOptions = [
    { value: 'all', label: 'Tất cả chi nhánh' },
    ...(branchesQuery.data ?? [])
      .filter((b) => chainId === 'all' || b.chainId === chainId)
      .map((b) => ({ value: b.id, label: b.name })),
  ];
  const chainOptions = [
    { value: 'all', label: 'Tất cả chuỗi' },
    ...(chainsQuery.data ?? []).map((c) => ({ value: c.id, label: c.name })),
  ];

  const branchColumns: ColumnsType<BranchPerformanceRow> = [
    {
      title: 'Chi nhánh',
      render: (_, row) => (
        <>
          <div style={{ fontWeight: 600 }}>{row.branchName}</div>
          <div style={{ fontSize: 12, color: tokens.color.ink3 }}>{row.chainName}</div>
        </>
      ),
    },
    {
      title: 'Doanh thu thành viên',
      align: 'right',
      width: 180,
      render: (_, row) => (
        <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {VND.format(row.memberRevenue)}đ
        </span>
      ),
    },
    {
      title: 'Bill có mã',
      align: 'right',
      width: 110,
      render: (_, row) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{VND.format(row.memberBillCount)}</span>
      ),
    },
    {
      title: 'Gắn cờ',
      align: 'right',
      width: 100,
      render: (_, row) =>
        row.flaggedScans > 0 ? (
          <Tag color="warning">{row.flaggedScans}</Tag>
        ) : (
          <span style={{ color: tokens.color.ink3 }}>0</span>
        ),
    },
  ];

  const dateLabel = date.format('DD/MM/YYYY');

  return (
    <>
      <PageHeader
        title="Bảng điều khiển"
        subtitle={`${dateLabel} · ${formatRefreshedAt(overview?.refreshedAt ?? null)}`}
        actions={
          <Space size={8} wrap>
            <DatePicker
              value={date}
              onChange={(v) => v && setDate(v)}
              allowClear={false}
              format="DD/MM/YYYY"
              aria-label="Ngày xem báo cáo"
              style={{ minHeight: 36 }}
            />
            {hq && (
              <Select
                aria-label="Lọc chuỗi"
                value={chainId}
                onChange={(v) => {
                  setChainId(v);
                  setBranchId('all');
                }}
                style={{ minWidth: 150, minHeight: 36 }}
                options={chainOptions}
              />
            )}
            {hq && (
              <Select
                aria-label="Lọc chi nhánh"
                value={branchId}
                onChange={setBranchId}
                style={{ minWidth: 180, minHeight: 36 }}
                options={branchOptions}
              />
            )}
          </Space>
        }
      />
      <PageBody>
        {overviewQuery.isError ? (
          <ErrorState error={overviewQuery.error} onRetry={() => void overviewQuery.refetch()} />
        ) : !overview ? (
          <LoadingState rows={6} />
        ) : (
          <>
            {/* Ô số */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 12,
                marginBottom: 18,
              }}
            >
              <Tile
                icon={<Money size={15} weight="fill" aria-hidden />}
                label="Doanh thu thành viên"
                value={<MoneyValue value={overview.memberRevenue} />}
                hint={<DeltaHint percent={overview.memberRevenueDeltaPercent} />}
              />
              <Tile
                icon={<Coins size={15} weight="fill" aria-hidden />}
                label="Điểm phát ra"
                value={VND.format(overview.pointsIssued)}
                hint={
                  <span>
                    đã đốt {VND.format(overview.pointsRedeemed)} điểm
                    {overview.burnRatePercent !== null ? `, tỷ lệ ${overview.burnRatePercent}%` : ''}
                  </span>
                }
              />
              <Tile
                icon={<Receipt size={15} weight="fill" aria-hidden />}
                label="Hóa đơn có mã thành viên"
                value={VND.format(overview.memberBillCount)}
                hint={
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    số hóa đơn tích điểm hôm nay <Info size={12} aria-hidden />
                  </span>
                }
                tooltip={overview.memberBillRateNote}
                muted
              />
              <Tile
                icon={<WarningCircle size={15} weight="fill" aria-hidden />}
                label="Cần bạn xử lý"
                value={VND.format(reviewCount)}
                hint={`${overview.manualScansPending} nhập tay · ${overview.reversalPendingScans} đảo điểm · ${overview.flaggedScans} gắn cờ`}
                danger={reviewCount > 0}
              />
            </div>

            <Row gutter={[18, 18]} style={{ marginBottom: 18 }}>
              {/* Biểu đồ doanh thu 7 ngày */}
              <Col xs={24} lg={16}>
              <Card
                style={{ borderRadius: tokens.radius.card, height: '100%' }}
                title={
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>Doanh thu thành viên 7 ngày</div>
                    <div style={{ fontSize: 12.5, fontWeight: 400, color: tokens.color.ink2 }}>
                      Đơn vị: triệu đồng. Cuối tuần tô màu đất.
                    </div>
                  </div>
                }
              >
                {trendQuery.isError ? (
                  <ErrorState error={trendQuery.error} onRetry={() => void trendQuery.refetch()} />
                ) : trendQuery.isPending ? (
                  <LoadingState rows={3} />
                ) : (
                  <TrendChart points={trendQuery.data.points} />
                )}
              </Card>
              </Col>

              {/* Phân bố hạng thẻ */}
              <Col xs={24} lg={8}>
              <Card
                style={{ borderRadius: tokens.radius.card, height: '100%' }}
                title={
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>Phân bố hạng thẻ</div>
                    <div style={{ fontSize: 12.5, fontWeight: 400, color: tokens.color.ink2 }}>
                      {tiersQuery.data
                        ? `${VND.format(tiersQuery.data.totalMembers)} thành viên`
                        : 'toàn hệ thống'}
                    </div>
                  </div>
                }
              >
                {tiersQuery.isError ? (
                  <ErrorState error={tiersQuery.error} onRetry={() => void tiersQuery.refetch()} />
                ) : tiersQuery.isPending ? (
                  <LoadingState rows={4} />
                ) : (
                  <TierBars buckets={tiersQuery.data.buckets} />
                )}
              </Card>
              </Col>
            </Row>

            <Row gutter={[18, 18]}>
              {/* Hiệu suất theo chi nhánh */}
              <Col xs={24} lg={16}>
              <Card
                styles={{ body: { padding: 0 } }}
                style={{ borderRadius: tokens.radius.card, height: '100%' }}
                title={
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>Hiệu suất theo chi nhánh</div>
                    <div style={{ fontSize: 12.5, fontWeight: 400, color: tokens.color.ink2 }}>
                      Ngày {dateLabel}, sắp theo doanh thu thành viên
                    </div>
                  </div>
                }
              >
                {branchesPerfQuery.isError ? (
                  <div style={{ padding: 16 }}>
                    <ErrorState
                      error={branchesPerfQuery.error}
                      onRetry={() => void branchesPerfQuery.refetch()}
                    />
                  </div>
                ) : (
                  <DataTable toolbar={false}
                    rowKey="branchId"
                    size="small"
                    loading={branchesPerfQuery.isFetching}
                    columns={branchColumns}
                    dataSource={branchesPerfQuery.data?.rows ?? []}
                    pagination={false}
                    scroll={{ x: 640 }}
                    locale={{ emptyText: 'Chưa có dữ liệu chi nhánh cho ngày này.' }}
                  />
                )}
              </Card>
              </Col>

              {/* Cần bạn xử lý */}
              <Col xs={24} lg={8}>
              <Card
                style={{ borderRadius: tokens.radius.card, height: '100%' }}
                title={<span style={{ fontSize: 15, fontWeight: 600 }}>Cần bạn xử lý</span>}
              >
                <Space direction="vertical" size={10} style={{ width: '100%' }}>
                  <ActionRow
                    tone="danger"
                    count={overview.flaggedScans}
                    title="giao dịch vượt ngưỡng bất thường"
                    hint="Cần đối chiếu tại màn đối soát."
                  />
                  <ActionRow
                    tone="warn"
                    count={overview.manualScansPending}
                    title="hóa đơn nhập tay chờ đối soát"
                    hint="Đối chiếu với báo cáo doanh thu POS."
                  />
                  <ActionRow
                    tone="info"
                    count={overview.reversalPendingScans}
                    title="yêu cầu đảo điểm chờ duyệt"
                    hint="Cần hội sở duyệt hai cấp."
                  />
                  <Button
                    type="primary"
                    block
                    icon={<ClipboardText size={16} aria-hidden />}
                    onClick={() => navigate('/reconciliation')}
                  >
                    Mở trang đối soát
                  </Button>
                </Space>
              </Card>
              </Col>
            </Row>

            <Row gutter={[18, 18]} style={{ marginTop: 18 }}>
              <Col xs={24}>
                <Card
                  style={{ borderRadius: tokens.radius.card }}
                  title={
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 600 }}>Điểm tồn (liability)</div>
                      <div style={{ fontSize: 12.5, fontWeight: 400, color: tokens.color.ink2 }}>
                        Nghĩa vụ điểm chưa tiêu, cộng dồn toàn hệ thống · đối chiếu sổ cái ↔ số dư
                      </div>
                    </div>
                  }
                >
                  {liabilityQuery.isError ? (
                    <ErrorState
                      error={liabilityQuery.error}
                      onRetry={() => void liabilityQuery.refetch()}
                    />
                  ) : liabilityQuery.isPending ? (
                    <LoadingState rows={3} />
                  ) : (
                    <LiabilityPanel data={liabilityQuery.data} />
                  )}
                </Card>
              </Col>
            </Row>

            <Typography.Paragraph type="secondary" style={{ fontSize: 12.5, marginTop: 20 }}>
              Số liệu làm mới theo lịch (job mỗi 15 phút) — mốc "{formatRefreshedAt(overview.refreshedAt)}" ở
              tiêu đề cho biết độ mới. Màn tự tải lại mỗi 30 giây. "% hóa đơn có mã" (KR1) cần tổng hóa đơn
              POS nên chưa tính được; ô hiện số đếm tuyệt đối. Chỉ số hàng chờ và phễu giới thiệu chưa có vì
              hai tính năng đó chưa mở. Điểm tồn là số cộng dồn trọn đời toàn hệ thống, không theo ngày
              hay chi nhánh.
            </Typography.Paragraph>
          </>
        )}
      </PageBody>
    </>
  );
}

function Tile({
  icon,
  label,
  value,
  hint,
  danger,
  muted,
  tooltip,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  hint: React.ReactNode;
  danger?: boolean;
  muted?: boolean;
  tooltip?: string;
}) {
  const body = (
    <div
      style={{
        background: tokens.color.surface,
        border: `1px solid ${tokens.color.line}`,
        borderRadius: tokens.radius.card,
        padding: tokens.space.cardPadding,
        boxShadow: tokens.shadow.card,
        opacity: muted ? 0.75 : 1,
        height: '100%',
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: tokens.color.ink2,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        {danger && (
          <span
            aria-hidden
            style={{ width: 7, height: 7, borderRadius: 999, background: tokens.color.danger, flex: 'none' }}
          />
        )}
        {/* Icon dùng CAM: thẻ KPI trước đây toàn xanh nên không có điểm nghỉ mắt. Đây là chỗ
            nhấn NỘI DUNG, không phải trạng thái — trạng thái vẫn là chấm đỏ `danger` ở trên. */}
        <span aria-hidden style={{ color: tokens.color.orange, display: 'inline-flex' }}>
          {icon}
        </span>
        {label}
      </div>
      <div
        style={{
          fontSize: 26,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          color: danger ? tokens.color.danger : tokens.color.ink,
          letterSpacing: '-0.02em',
          marginTop: 4,
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 12, color: tokens.color.ink3, marginTop: 4 }}>{hint}</div>
    </div>
  );
  return tooltip ? (
    <Tooltip title={tooltip}>
      <div style={{ height: '100%' }}>{body}</div>
    </Tooltip>
  ) : (
    body
  );
}

function DeltaHint({ percent }: { percent: number | null }) {
  if (percent === null) {
    return <span>so hôm trước —</span>;
  }
  const up = percent >= 0;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        color: up ? tokens.color.ok : tokens.color.danger,
      }}
    >
      {up ? <ArrowUp size={12} weight="bold" aria-hidden /> : <ArrowDown size={12} weight="bold" aria-hidden />}
      {up ? 'tăng' : 'giảm'} {Math.abs(percent)}% so với hôm trước
    </span>
  );
}

function TrendChart({ points }: { points: { date: string; memberRevenue: number }[] }) {
  const max = Math.max(1, ...points.map((p) => p.memberRevenue));
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 160 }}>
        {points.map((p) => {
          const d = dayjs(p.date);
          const weekend = d.day() === 0 || d.day() === 6;
          const heightPct = Math.round((p.memberRevenue / max) * 100);
          return (
            <div key={p.date} style={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
              <Tooltip title={`${d.format('DD/MM')}: ${VND.format(p.memberRevenue)}đ`}>
                <div style={{ height: 132, display: 'flex', alignItems: 'flex-end' }}>
                  <div
                    style={{
                      width: '100%',
                      height: `${Math.max(heightPct, p.memberRevenue > 0 ? 4 : 1)}%`,
                      background: weekend ? tokens.color.orange : tokens.color.green600,
                      borderRadius: '6px 6px 0 0',
                    }}
                  />
                </div>
              </Tooltip>
              <div style={{ fontSize: 11, color: tokens.color.ink3, marginTop: 6 }}>
                {['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'][d.day()]}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 12, color: tokens.color.ink2 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <i style={{ width: 10, height: 10, borderRadius: 3, background: tokens.color.green600 }} /> Ngày thường
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <i style={{ width: 10, height: 10, borderRadius: 3, background: tokens.color.orange }} /> Cuối tuần
        </span>
      </div>
    </div>
  );
}

function TierBars({ buckets }: { buckets: TierDistributionBucket[] }) {
  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      {buckets.map((b) => (
        <div key={b.tierCode ?? 'unranked'} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 92, fontSize: 12.5, color: tokens.color.ink2, flex: 'none' }}>
            {b.tierName}
          </span>
          <div
            style={{
              flex: 1,
              height: 10,
              background: tokens.color.surface2,
              borderRadius: 999,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${b.percent}%`,
                height: '100%',
                background: tierColorFor(b.tierCode),
                borderRadius: 999,
              }}
            />
          </div>
          <span
            style={{
              width: 92,
              textAlign: 'right',
              fontSize: 12.5,
              fontVariantNumeric: 'tabular-nums',
              color: tokens.color.ink2,
              flex: 'none',
            }}
          >
            {b.percent}% · {VND.format(b.memberCount)}
          </span>
        </div>
      ))}
    </Space>
  );
}

/** Nhãn tiếng Việt cho loại bút toán (khớp PointEntryType bên BE). */
const ENTRY_LABEL: Record<string, string> = {
  Earn: 'Tích chi tiêu',
  WelcomeBonus: 'Quà hóa đơn đầu',
  ReviewBonus: 'Thưởng đánh giá',
  ReferralBonus: 'Thưởng giới thiệu',
  Redeem: 'Đổi quà',
  Reversal: 'Đảo điểm',
  Adjust: 'Điều chỉnh tay',
  Expire: 'Hết hạn',
  SessionShare: 'Chia điểm bàn',
};

function LiabilityPanel({ data }: { data: DashboardLiability }) {
  const matches = data.balanceMatchesLedger;
  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <Tile
          icon={<Coins size={15} weight="fill" aria-hidden />}
          label="Điểm tồn (chưa tiêu)"
          value={VND.format(data.outstandingFromLedger)}
          hint={`${VND.format(data.membersWithBalance)} khách còn điểm`}
        />
        <Tile
          icon={<ArrowUp size={15} weight="bold" aria-hidden />}
          label="Đã phát ra (trọn đời)"
          value={VND.format(data.totalIssued)}
          hint="tổng mọi bút toán cộng điểm"
        />
        <Tile
          icon={<ArrowDown size={15} weight="bold" aria-hidden />}
          label="Đã đốt (trọn đời)"
          value={VND.format(data.totalBurned)}
          hint={data.burnRatePercent !== null ? `tỷ lệ đốt ${data.burnRatePercent}%` : 'chưa phát điểm nào'}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 12.5, color: tokens.color.ink2 }}>Đối chiếu sổ cái ↔ số dư:</span>
        {matches ? (
          <StatusTag color="success">Khớp</StatusTag>
        ) : (
          <StatusTag color="error">Lệch {VND.format(Math.abs(data.discrepancy))} điểm</StatusTag>
        )}
        {!matches && (
          <span style={{ fontSize: 12, color: tokens.color.ink3 }}>
            sổ cái {VND.format(data.outstandingFromLedger)} · số dư{' '}
            {VND.format(data.outstandingFromBalances)} — hai số đang lệch, cần đối soát
          </span>
        )}
      </div>

      <div style={{ fontSize: 12.5, fontWeight: 600, color: tokens.color.ink2, marginBottom: 8 }}>
        Bóc tách theo loại bút toán
      </div>
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        {data.breakdown.map((b) => {
          const positive = b.spendablePoints >= 0;
          return (
            <div key={b.entryType} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 150, fontSize: 12.5, color: tokens.color.ink2, flex: 'none' }}>
                {ENTRY_LABEL[b.entryType] ?? b.entryType}
              </span>
              <span
                style={{
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: 600,
                  color: positive ? tokens.color.ok : tokens.color.danger,
                }}
              >
                {positive ? '+' : '−'}
                {VND.format(Math.abs(b.spendablePoints))}
              </span>
            </div>
          );
        })}
      </Space>
    </div>
  );
}

function ActionRow({
  tone,
  count,
  title,
  hint,
}: {
  tone: 'danger' | 'warn' | 'info';
  count: number;
  title: string;
  hint: string;
}) {
  const bg =
    tone === 'danger' ? tokens.color.danger50 : tone === 'warn' ? tokens.color.warn50 : tokens.color.info50;
  const fg =
    tone === 'danger' ? tokens.color.danger : tone === 'warn' ? tokens.color.warn : tokens.color.info;
  const muted = count === 0;
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        padding: '10px 12px',
        borderRadius: tokens.radius.control,
        background: muted ? tokens.color.surface2 : bg,
        opacity: muted ? 0.7 : 1,
      }}
    >
      <WarningCircle size={18} weight="fill" aria-hidden style={{ color: muted ? tokens.color.ink3 : fg, flex: 'none' }} />
      <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>
        <b style={{ color: tokens.color.ink }}>
          {VND.format(count)} {title}
        </b>
        <div style={{ color: tokens.color.ink2 }}>{hint}</div>
      </div>
    </div>
  );
}
